import React, { useState } from 'react';

/**
 * Extracts initials from a user's full name, username, or email.
 * E.g., "Juan Pérez" -> "JP", "Administrador" -> "AD", "carlos@rutex.com" -> "CA"
 */
export const getInitials = (name, fallback = 'AD') => {
  if (!name || typeof name !== 'string') return fallback;
  const cleanName = name.replace(/@.+/, '').trim();
  const parts = cleanName.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Returns a consistent gradient background based on user name string.
 */
export const getAvatarColor = (name) => {
  const gradients = [
    'linear-gradient(135deg, #183eeb 0%, #00024f 100%)', // Rutex Brand Primary
    'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)', // Cyan / Blue
    'linear-gradient(135deg, #10b981 0%, #047857 100%)', // Emerald
    'linear-gradient(135deg, #8b5cf6 0%, #5b21b6 100%)', // Purple
    'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)', // Amber
    'linear-gradient(135deg, #ec4899 0%, #be185d 100%)', // Pink
  ];
  if (!name) return gradients[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return gradients[Math.abs(hash) % gradients.length];
};

/**
 * UserAvatar Component
 * Displays a clean, circular user avatar with initials or image, without dots or outer square containers.
 */
const UserAvatar = ({
  user,
  name,
  size = 'md',
  className = '',
  style = {}
}) => {
  const displayName = name || user?.nombre_completo || user?.name || user?.username || user?.email || 'Usuario';
  const avatarUrl = user?.avatar || user?.foto_perfil || user?.profile_photo_url || null;
  const initials = getInitials(displayName);
  const [imgError, setImgError] = useState(false);

  const sizeMap = {
    xs: { width: 28, height: 28, fontSize: '0.75rem' },
    sm: { width: 34, height: 34, fontSize: '0.85rem' },
    md: { width: 44, height: 44, fontSize: '1rem' },
    lg: { width: 64, height: 64, fontSize: '1.4rem' },
    xl: { width: 100, height: 100, fontSize: '2rem' },
  };

  const currentSize = typeof size === 'number'
    ? { width: size, height: size, fontSize: `${Math.round(size * 0.38)}px` }
    : (sizeMap[size] || sizeMap.md);

  const backgroundGradient = getAvatarColor(displayName);

  return (
    <div
      className={`user-avatar rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 text-white fw-bold shadow-sm ${className}`}
      style={{
        width: `${currentSize.width}px`,
        height: `${currentSize.height}px`,
        minWidth: `${currentSize.width}px`,
        minHeight: `${currentSize.height}px`,
        fontSize: currentSize.fontSize,
        background: (avatarUrl && !imgError) ? 'transparent' : backgroundGradient,
        letterSpacing: '0.5px',
        userSelect: 'none',
        overflow: 'hidden',
        lineHeight: 1,
        ...style
      }}
      title={displayName}
    >
      {avatarUrl && !imgError ? (
        <img
          src={avatarUrl}
          alt={displayName}
          className="w-100 h-100 rounded-circle"
          style={{ objectFit: 'cover' }}
          onError={() => setImgError(true)}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
};

export default UserAvatar;
